import _ from 'lodash'
import { IDB_deleteAll, IDB_dictReadAll, IDB_rowsReadAll } from './IDB.queries'
import { IDB_calendarEventRead } from './IDB.table.calendarEvent'
import { IDB_fileLookupWrite } from './IDB.table.file'
import { IDB_locationOwnershipsReadAll, IDB_locationOwnershipsWrite } from './IDB.table.locationOwnership'
import { IDB_locationTimelineEventsReadAll, IDB_locationTimelineEventsWrite } from './IDB.table.locationTimelineEvent'
import { IDB_processFlowsWrite } from './IDB.table.processFlow'
import { IDB_reportsReadAll, IDB_reportsWrite } from './IDB.table.report'
import { IDB_taskReportsReadAll, IDB_taskReportsWrite } from './IDB.table.taskReport'
import { IDB_workflowsReadAll, IDB_workflowsWrite } from './IDB.table.workflow'
import { IDB_globalDb } from './IDB'
import { Imag_emptyPNG } from '../Imag/Imag.emptyPNG'
import type { Imag_LegacyFile } from '../Imag/Imag.LegacyFile'
import { Imag_readDataURIFromFileAndDelete } from '../Imag/Imag.readFromFile'
import { Imag_savePhoto } from '../Imag/Imag.Photo'
import { PF_migrateFromLocalStorage } from '../PF/PF.migrate'
import { Rpts_openReportRestoreFromBackups } from '../Rpts/Rpts.OpenReport'
import { Rpts_createClientId } from '../Rpts/Rpts.types'
import { UF_migrateUnitFormToUuids } from '../UF/UF.migrate'
import { UPF_migrateUnitReportToUuids } from '../UPF/UPF.migrate'
import { isFlowWorkflow } from '../../../app-core/lib/workflow/Workflow'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import { IDB_slugs, IDB_syncParamsActivityWorkflows, IDB_syncParamsFlowWorkflows } from './IDB.dexie'
import { assert, is } from 'tsafe'
import type { SyncFlowWorkflow } from '../../../shared/domain/workflows/types.no-deps'
import { isConcreteStep } from '../../../shared/domain/flows'

/*  Any migrations which need to happen within the 'onupgradeneeded'
    (eg table creations and deletions) go in the IDB.initialise file.
    All other migrations live here.
    This allows us to more easily
        - Serialise the migrations (preventing race conditions and conflicts)
        - Use async await syntax and our query wrappers
*/

export async function IDB_runAsyncMigrations(oldVersion: number) {
    const logger = Logg_createLogger(`IDB_runAsyncMigrations`)

    // Move the pfs out of localStorage
    if (oldVersion < 600) {
        try {
            await IDB_processFlowsWrite(PF_migrateFromLocalStorage())
            localStorage.removeItem('process_flow_v2')
        } catch (err) {
            logger.error(err, {}, { context: 'failed to migrate pfs from local storage' })
        }
    }

    // Migrate the unitForm to use instanceIds instead of unitIds
    if (oldVersion < 620) {
        try {
            const taskreports = (await IDB_taskReportsReadAll()).map(UF_migrateUnitFormToUuids)
            await IDB_taskReportsWrite(taskreports)
        } catch (err) {
            logger.error(err, {}, { context: 'failed to migrate unit form to uuids' })
        }
    }

    // Migrate the unitPf taskReports to use instanceIds instead of unitIds
    if (oldVersion < 630) {
        try {
            const taskreports = (await IDB_taskReportsReadAll()).map(UPF_migrateUnitReportToUuids)
            await IDB_taskReportsWrite(taskreports)
        } catch (err) {
            logger.error(err, {}, { context: 'failed to migrate unit report to uuids' })
        }
    }

    // Migrate forename and surname to name and nickname
    if (oldVersion < 640) {
        try {
            const timelines = await IDB_locationTimelineEventsReadAll()
            for (const timeline of timelines) {
                for (const event of timeline.events) {
                    if (event.type === 'smartdata') continue

                    const author = event.author as any
                    event.author = {
                        name: `${author.forename} ${author.surname}`,
                    }
                }
            }
            await IDB_locationTimelineEventsWrite(timelines)
        } catch (err) {
            logger.error(err, {}, { context: 'failed to migrate forename and surname in timeline events' })
        }

        try {
            const locationOwnerships = await IDB_locationOwnershipsReadAll()
            for (const locationOwnership of locationOwnerships) {
                for (const owner of locationOwnership.owners) {
                    const oldOwner = owner as any
                    owner.name = `${oldOwner.forename} ${oldOwner.surname}`
                    delete oldOwner.forename
                    delete oldOwner.surname
                }
            }
            await IDB_locationOwnershipsWrite(locationOwnerships)
        } catch (err) {
            logger.error(err, {}, { context: 'failed to migrate forename and surname in location ownerships' })
        }
    }

    // Migrate any remaining report.event strings to event object
    if (oldVersion < 670) {
        try {
            const reportPromises = (await IDB_reportsReadAll()).map(async (report) => {
                if (report.uploaded) return report
                if (!_.isString(report.event) && !_.isNumber(report.event)) return report

                report.event = await IDB_calendarEventRead(report.event)
                return report
            })

            const reports = await Promise.all(reportPromises)

            await IDB_reportsWrite(logger, reports)
        } catch (err) {
            logger.error(err)
        }
    }

    // Migrate workflows to the new format
    if (oldVersion < 690) {
        try {
            type oldWorkflowType = {
                id: number
                name: string
                items: Array<{
                    id: number
                    action: number
                    list: number
                    position: number
                    dependencies: number[]
                }>
                targets: number[]
            }

            const workflows = (await IDB_workflowsReadAll()).map((workflow) => {
                const { id, name, items } = workflow as any as oldWorkflowType
                return {
                    id,
                    name,
                    type: 'task',
                    activities: items.map((step) => {
                        const { id, position, dependencies } = step
                        return { id, position, dependencies }
                    }),
                }
            })

            await IDB_workflowsWrite(logger, workflows as any)
        } catch (err) {
            logger.error(err)
        }
    }

    if (oldVersion < 720) {
        try {
            const backups = await IDB_rowsReadAll<any>(IDB_globalDb, 'backup' as any)
            await Rpts_openReportRestoreFromBackups(backups)
        } catch (err) {
            logger.error(err)
        }
    }

    if (oldVersion < 730) {
        try {
            const reports = await IDB_reportsReadAll()
            await IDB_reportsWrite(
                logger,
                reports.map((report) => ({
                    ...report,
                    clientId: Rpts_createClientId(),
                }))
            )
        } catch (err) {
            logger.error(err)
        }
    }

    if (oldVersion < 740) {
        try {
            const workflows = await IDB_workflowsReadAll()

            for (const workflow of workflows) {
                if (!isFlowWorkflow(workflow)) continue

                for (const step of workflow.flow.steps) {
                    if (!isConcreteStep(step)) {
                        continue
                    }
                    step.conditionalRoutes = []
                    step.defaultRoute = 'Step.NextStep'
                }
            }

            await IDB_workflowsWrite(logger, workflows)
        } catch (error) {
            logger.error(error)
        }
    }

    if (oldVersion < 760) {
        try {
            // Update all workflows to have an adhoc flag
            const workflows = await IDB_workflowsReadAll()
            for (const workflow of workflows) {
                assert(is<SyncFlowWorkflow>(workflow))
                workflow.adHoc = true
            }
            await IDB_workflowsWrite(logger, workflows)
        } catch (error) {
            logger.error(error)
        }
    }

    if (oldVersion < 770) {
        try {
            const files = await IDB_rowsReadAll<Imag_LegacyFile>(IDB_globalDb, 'files')
            for (const file of files) {
                try {
                    const dataUrl =
                        file.content ||
                        (file.path && (await Imag_readDataURIFromFileAndDelete(file.path))) ||
                        Imag_emptyPNG
                    const fileName = await Imag_savePhoto(null, dataUrl)
                    await IDB_fileLookupWrite({ fileId: file.fileId as number, fileName })
                } catch (error) {
                    logger.error(error)
                }
            }
        } catch (error) {
            logger.error(error)
        }
    }

    if (oldVersion < 780) {
        try {
            const slugs = await IDB_dictReadAll<string>(IDB_globalDb, 'slugs')
            await IDB_slugs().set(slugs)
            await IDB_deleteAll(IDB_globalDb, 'slugs')
        } catch (error) {
            logger.error(error)
        }
    }

    if (oldVersion < 790) {
        // Reset workflows sync params so that they are all refreshed.
        // We need to do this because the model changed - it now includes associated procedures.
        try {
            await IDB_syncParamsActivityWorkflows().reset()
            await IDB_syncParamsFlowWorkflows().reset()
        } catch (error) {
            logger.error(error)
        }
    }
}
